import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FaArrowRight, FaLayerGroup } from 'react-icons/fa'
import { MdOutlineKeyboardArrowRight } from 'react-icons/md'
import api from '../../api/api'
import { resolveImageUrl } from '../../utilies/imageUrl'

let categoryTreeCache = null
let categoryTreeRequest = null

const loadCategoryTree = () => {
    if (categoryTreeCache) return Promise.resolve(categoryTreeCache)
    if (!categoryTreeRequest) {
        categoryTreeRequest = api.get('/home/category-tree')
            .then(({ data }) => {
                categoryTreeCache = Array.isArray(data?.categories) ? data.categories : []
                return categoryTreeCache
            })
            .finally(() => { categoryTreeRequest = null })
    }
    return categoryTreeRequest
}

const categoryLink = category => `/kateqoriya/${encodeURIComponent(category.slug)}`
const categoryLabel = category => category?.displayName || category?.name || 'Kateqoriya'
const listingLabel = count => `${Number(count) || 0} elan`

const CategoryVisual = ({ category, size = 'md' }) => {
    const box = size === 'lg' ? 'h-11 w-11 rounded-xl' : 'h-9 w-9 rounded-lg'
    if (category?.image) {
        return <img src={resolveImageUrl(category.image)} alt='' className={`${box} shrink-0 border border-slate-200 bg-slate-50 object-cover`} />
    }
    return <span aria-hidden className={`${box} flex shrink-0 items-center justify-center bg-brand-50 text-lg text-brand-700`}>{category?.icon || <FaLayerGroup size={14}/>}</span>
}

const DesktopCategoryMegaMenu = ({ open, onClose }) => {
    const [categories, setCategories] = useState(categoryTreeCache || [])
    const [selectedId, setSelectedId] = useState(categoryTreeCache?.[0]?._id || '')
    const [loading, setLoading] = useState(!categoryTreeCache)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (!open || categoryTreeCache) return
        let active = true
        setLoading(true)
        setFailed(false)
        loadCategoryTree()
            .then(rows => {
                if (!active) return
                setCategories(rows)
                setSelectedId(current => current || rows[0]?._id || '')
            })
            .catch(() => { if (active) setFailed(true) })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [open])

    const selected = useMemo(
        () => categories.find(category => String(category._id) === String(selectedId)) || categories[0] || null,
        [categories, selectedId]
    )

    if (!open) return null

    return (
        <section
            id='desktop-category-mega-menu'
            role='region'
            aria-label='Kateqoriyalar menyusu'
            className='absolute left-1/2 top-full z-50 w-[calc(100%-2rem)] max-w-marketplace -translate-x-1/2 pt-2 md-lg:hidden'
        >
            <div className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_22px_55px_rgba(15,23,42,0.16)]'>
                {loading ? (
                    <div className='grid min-h-[320px] grid-cols-[300px_1fr] gap-6 p-6'>
                        <div className='space-y-2'>{[1,2,3,4,5].map(item => <div key={item} className='h-12 animate-pulse rounded-xl bg-slate-100'/>)}</div>
                        <div className='animate-pulse rounded-2xl bg-slate-100'/>
                    </div>
                ) : failed || categories.length === 0 ? (
                    <div className='flex min-h-[190px] flex-col items-center justify-center px-6 py-8 text-center'>
                        <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700'><FaLayerGroup/></span>
                        <p className='mt-3 text-sm font-bold text-slate-800'>Kateqoriyalar yüklənmədi</p>
                        <Link to='/kateqoriyalar' onClick={onClose} className='mt-3 text-sm font-black text-brand-700 hover:text-brand-800'>Bütün kateqoriyalar <FaArrowRight className='ml-1 inline' size={10}/></Link>
                    </div>
                ) : (
                    <div className='grid h-[calc(100vh-150px)] min-h-0 max-h-[520px] grid-cols-[310px_1fr] overflow-hidden'>
                        <div className='h-full min-h-0 overflow-y-auto overscroll-contain border-r border-slate-100 bg-slate-50/70 p-3 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5'>
                            <div className='flex items-center justify-between px-2 pb-2 pt-1'>
                                <p className='text-[11px] font-black uppercase tracking-wider text-slate-400'>Kateqoriyalar</p>
                                <Link to='/kateqoriyalar' onClick={onClose} className='text-[11px] font-bold text-brand-700 hover:text-brand-800'>Hamısı</Link>
                            </div>
                            <div className='space-y-1'>
                                {categories.map(category => {
                                    const active = String(category._id) === String(selected?._id)
                                    return (
                                        <button
                                            key={category._id}
                                            type='button'
                                            onMouseEnter={() => setSelectedId(category._id)}
                                            onFocus={() => setSelectedId(category._id)}
                                            onClick={() => setSelectedId(category._id)}
                                            className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? 'border-brand-200 bg-white text-brand-800 shadow-sm' : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-white'}`}
                                        >
                                            <CategoryVisual category={category}/>
                                            <span className='min-w-0 flex-1'>
                                                <span className='block truncate text-sm font-black'>{categoryLabel(category)}</span>
                                                <span className='mt-0.5 block text-[11px] font-semibold text-slate-400'>{listingLabel(category.productCount)}</span>
                                            </span>
                                            <MdOutlineKeyboardArrowRight className={active ? 'text-brand-600' : 'text-slate-300'} size={17}/>
                                        </button>
                                    )
                                })}
                            </div>
                        </div>

                        <div className='h-full min-h-0 min-w-0 overflow-y-auto overscroll-contain p-6 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5'>
                            <div className='flex items-start justify-between gap-5 border-b border-slate-100 pb-5'>
                                <div className='flex min-w-0 items-center gap-3'>
                                    <CategoryVisual category={selected} size='lg'/>
                                    <div className='min-w-0'>
                                        <h2 className='truncate text-xl font-black text-slate-900'>{categoryLabel(selected)}</h2>
                                        <p className='mt-1 text-sm font-semibold text-slate-500'>{listingLabel(selected?.productCount)} · {Number(selected?.childCount) || 0} bölmə</p>
                                    </div>
                                </div>
                                <Link to={categoryLink(selected)} onClick={onClose} className='inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-black text-white shadow-sm transition hover:bg-brand-700'>Hamısına bax <FaArrowRight size={10}/></Link>
                            </div>

                            {(selected?.children || []).length > 0 ? (
                                <div className='mt-5'>
                                    <p className='mb-3 text-[11px] font-black uppercase tracking-wider text-slate-400'>Bölmələr</p>
                                    <div className='grid grid-cols-3 gap-3 xl:grid-cols-2'>
                                        {selected.children.map(child => (
                                            <Link key={child._id} to={categoryLink(child)} onClick={onClose} className='group flex min-h-[78px] items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-brand-300 hover:bg-brand-50/50 hover:shadow-sm'>
                                                <CategoryVisual category={child}/>
                                                <span className='min-w-0 flex-1'>
                                                    <span className='block truncate text-sm font-black text-slate-800 group-hover:text-brand-700'>{categoryLabel(child)}</span>
                                                    <span className='mt-1 block text-[11px] font-semibold text-slate-400'>{listingLabel(child.productCount)}</span>
                                                </span>
                                                <MdOutlineKeyboardArrowRight className='shrink-0 text-slate-300 group-hover:text-brand-500'/>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className='mt-8 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-9 text-center'>
                                    <p className='text-sm font-semibold text-slate-500'>Bu kateqoriyada hələ bölmə yoxdur.</p>
                                    <Link to={categoryLink(selected)} onClick={onClose} className='mt-4 inline-flex items-center gap-2 rounded-xl border border-brand-200 bg-white px-4 py-2.5 text-xs font-black text-brand-700 hover:bg-brand-50'>Kateqoriyaya bax <FaArrowRight size={10}/></Link>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default DesktopCategoryMegaMenu
